/**
 * Sortable Words Table Component
 *
 * Displays a table of words with drag-and-drop reordering capability.
 * Uses @dnd-kit for accessible drag-and-drop functionality.
 */

import { useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { WordInList, WordOrderItem } from '../types/word.types';

interface SortableWordsTableProps {
  words: WordInList[];
  onEdit: (word: WordInList) => void;
  onRemove: (word: WordInList) => void;
  onReorder: (wordOrders: WordOrderItem[]) => void;
  isRemoving?: boolean;
  isReordering?: boolean;
}

interface SortableRowProps {
  word: WordInList;
  onEdit: (word: WordInList) => void;
  onRemove: (word: WordInList) => void;
  isRemoving?: boolean;
  isDragging?: boolean;
}

/**
 * Get unique ID for a word (composite key)
 */
function getWordId(word: WordInList): string {
  return `${word.word}:${word.languageCode}`;
}

/**
 * Sortable table row component
 */
function SortableRow({ word, onEdit, onRemove, isRemoving, isDragging }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isCurrentDragging,
  } = useSortable({ id: getWordId(word) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isCurrentDragging ? 0.5 : 1,
    backgroundColor: isCurrentDragging ? '#f3f4f6' : undefined,
  };

  const getTranslationCount = (translations: Record<string, string> | null) => {
    if (!translations) return 0;
    return Object.keys(translations).length;
  };

  const getPronunciationCount = (
    pronunciations: Record<string, { ipa: string | null; audioUrl: string | null }> | null
  ) => {
    if (!pronunciations) return 0;
    return Object.keys(pronunciations).length;
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      data-testid={`word-row-${word.word}`}
      className={isDragging ? 'bg-gray-50' : ''}
    >
      {/* Drag Handle */}
      <td className="px-2 py-4 w-10">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
          title="Drag to reorder"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path
              fillRule="evenodd"
              d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </td>
      {/* Order Number */}
      <td className="px-4 py-4 text-sm text-gray-500 w-12">{word.order + 1}</td>
      {/* Word */}
      <td className="px-4 py-4">
        <div className="text-sm font-medium text-gray-900">{word.word}</div>
        <div className="text-xs text-gray-500">{word.languageCode}</div>
      </td>
      {/* Image */}
      <td className="px-4 py-4 w-20">
        {word.imageUrl ? (
          <img src={word.imageUrl} alt={word.word} className="h-10 w-10 object-cover rounded" />
        ) : (
          <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center">
            <span className="text-gray-400 text-xs">No img</span>
          </div>
        )}
      </td>
      {/* Example Sentence */}
      <td className="px-4 py-4">
        <div className="text-sm text-gray-600 max-w-md truncate">
          {word.exampleSentence || (
            <span className="text-gray-400 italic">No example sentence</span>
          )}
        </div>
      </td>
      {/* Coverage */}
      <td className="px-4 py-4 w-28">
        <div className="flex gap-2">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              getTranslationCount(word.translations) > 0
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-500'
            }`}
            title="Translations"
          >
            T: {getTranslationCount(word.translations)}
          </span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              getPronunciationCount(word.pronunciations) > 0
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-500'
            }`}
            title="Pronunciations"
          >
            P: {getPronunciationCount(word.pronunciations)}
          </span>
        </div>
      </td>
      {/* Actions */}
      <td className="px-4 py-4 text-sm font-medium space-x-2 w-32">
        <button onClick={() => onEdit(word)} className="text-blue-600 hover:text-blue-900">
          Edit
        </button>
        <button
          onClick={() => onRemove(word)}
          disabled={isRemoving}
          className="text-red-600 hover:text-red-900 disabled:opacity-50"
        >
          Remove
        </button>
      </td>
    </tr>
  );
}

export function SortableWordsTable({
  words,
  onEdit,
  onRemove,
  onReorder,
  isRemoving,
  isReordering,
}: SortableWordsTableProps) {
  const [isDragging, setIsDragging] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get sorted items with their IDs
  const sortedWords = useMemo(() => {
    return [...words].sort((a, b) => a.order - b.order);
  }, [words]);

  const wordIds = useMemo(() => sortedWords.map(getWordId), [sortedWords]);

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = wordIds.indexOf(active.id as string);
    const newIndex = wordIds.indexOf(over.id as string);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Calculate new order for all items
    const reorderedWords = arrayMove(sortedWords, oldIndex, newIndex);

    // Create word orders array with new positions
    const wordOrders: WordOrderItem[] = reorderedWords.map((word, index) => ({
      card_id: word.cardId,
      word: word.word,
      language_code: word.languageCode,
      order: index,
    }));

    onReorder(wordOrders);
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {isReordering && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
          <span className="text-sm text-blue-700">Saving new order...</span>
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                {/* Drag handle column */}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Word
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Image
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Example Sentence
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                Coverage
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <SortableContext items={wordIds} strategy={verticalListSortingStrategy}>
              {sortedWords.map((word) => (
                <SortableRow
                  key={getWordId(word)}
                  word={word}
                  onEdit={onEdit}
                  onRemove={onRemove}
                  isRemoving={isRemoving}
                  isDragging={isDragging}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
      {sortedWords.length > 1 && (
        <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">
          Drag rows to reorder words
        </div>
      )}
    </div>
  );
}
